<script lang="ts" setup>
import { useRouter, useRoute } from 'nuxt/app'
import { useI18n } from 'vue-i18n'
import { useScreen } from '@/composables/useScreen'
import { dateUtils } from '@/helpers'
import IconBell from '@/assets/icons/bell.svg'
import IconPlus from '@/assets/icons/plus.svg'
import IconMinus from '@/assets/icons/minus.svg'
import IconDoubleCheck from '@/assets/icons/double-checked.svg'

const axios = useAxios()
const { isMobileView } = useScreen()
const router = useRouter()
const route = useRoute()
const { signOut, session, user } = useSession()
const confirmLogoutVisible = ref(false)
const webSocket = useNuxtApp().$webSocket
const { t, locale, availableLocales, messages } = useI18n()
const { formatDateTimeFromNow } = dateUtils()
const profilePopoverRef = ref()
const notificationPopoverRef = ref()
const isNotificationsOpen = ref(false)
const isMobileMenuOpened = ref(false)
const isMobileProfileExpanded = ref(false)
const localePath = useLocalePath()
const isHomePage = computed(() => route.fullPath === localePath('/'))
const { on } = useEventBus()

// this is needed for elementPlus SSR.
const isClient = ref(false)
onMounted(() => {
  isClient.value = true
})

// Notifications state
const allNotifications = ref<any[]>([])
const socket = computed(() => webSocket?.socket)
const initialNotifications = computed(() => webSocket?.notifications)
const MAX_NOTIFICATIONS = 5

// Range notification to max allowed
const notifications = computed(() => allNotifications.value.slice(0, MAX_NOTIFICATIONS))

// Refresh notifications from API
async function refreshNotifications() {
  try {
    const response = await axios.get('/internal/notifications')
    allNotifications.value = response.data
  } catch (error) {
    console.error('Failed to refresh notifications:', error)
  }
}

// Listen for notification updates
on('notifications-updated', refreshNotifications)

watchEffect(() => {
  // Saving notification from websocket(real time)
  if (socket.value) {
    socket.value.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data)
        allNotifications.value.unshift(data)
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
      }
    }
  }

  // Initial notifications from endpoint(when user was offline)
  if (initialNotifications.value?.length) {
    allNotifications.value = [...initialNotifications.value]
  }

  // Clear all notifications when user changed
  if (!user.value) {
    allNotifications.value = []
  }
})

const extractMessage = (messageObject: any) => messageObject?.body?.static || messageObject?.b?.s

const handleLocaleChange = (payload: string) => {
  // translate route params
  const params = Object.entries(route.params).reduce((acc, [key, value]) => {
    // Skip the translation for specific routes
    if (['firmLawyerId', 'id', 'slug'].includes(key)) return { ...acc, [key]: value };

    // find locale key
    const translationKey = Object.entries(messages.value[locale.value]).map(([mKey, mValue]) => {
      // non binary value stored in `message.body.static`
      const value = extractMessage(mValue)
      if (value && typeof route.params[key] === 'string' && value.toLowerCase() === route.params[key]?.toLowerCase()) {
        return mKey
      }
      
    }).filter(Boolean)[0] // filter array from undefined items and retrieve first

    if (!translationKey) return acc

    // assign translation if exists
    const message = extractMessage(messages.value[payload][translationKey])

    return message ? {
      ...acc,
      [key]: message ? message.toLowerCase() : null
    } : { ...acc }
  }, {})

  if (Object.values(params).length !== Object.values(route.params).length) {
    console.error('Route params mismatch. Translation failed')
    return
  }

  // update new route
  router.push({
    name: `${route?.name?.toString().split('___')[0]}___${payload}`,
    query: route.query,
    params
  })
}

// Forcing browser to rerender image when it is updated.
const getUpdatedPhotoUrl = (url: string) => {
  const timestamp = new Date().getTime()
  return `${url}?t=${timestamp}`
}

const isSessionAvailable = computed(() => {
  return Boolean(user.value)
})

async function handleRedirectToService(visitId: number, notificationId: number) {
  const localePrefix = locale.value === 'en' ? '/en' : ''
  const servicePath = `${localePrefix}/${t('services').toLowerCase()}/${visitId}`
  const currentPath = route.path

  if (currentPath !== servicePath) {
    await handleCleanButton(notificationId)

    if (isMobileView.value) {
      isMobileMenuOpened.value = false
      isNotificationsOpen.value = false
    } else {
      queueMicrotask(() => (window as any).document?.activeElement?.blur?.())
      notificationPopoverRef.value?.hide()
    }

    await router.push(servicePath)
  }
}

async function handleCleanButton(id: number) {
  try {
    await axios.post(`/internal/notifications/${id}/view`)
    allNotifications.value = allNotifications.value.filter(n => n.id !== id)
  } catch (error) {
    console.error('Failed to clear notification:', error)
  }
}

async function handleClearAll() {
  try {
    await axios.post('/internal/notifications/view/all');
    allNotifications.value = [];
  } catch (error) {
    console.error('Failed to clear all notifications:', error);
  }
}

const dialogConfirm = () => {
  confirmLogoutVisible.value = false
  isMobileMenuOpened.value = false
  profilePopoverRef.value?.hide()

  signOut()
}
</script>
<template>
  <div class="container font-branding font-semibold z-[100]">
    <teleport to="body">
      <GTModal
        :isOpen="confirmLogoutVisible"
        :width="440"
        :closeIcon="true"
        @close="confirmLogoutVisible = false"
        @confirm="dialogConfirm"
      >
        <template #header>
          <div class="mb-3 -mt-2">
            {{ $t('confirmation') }}
          </div>
        </template>

        <template #content>
          <div class="my-6">
            {{ $t('do_you_really_want_to') }} <strong>{{ $t('logout') }}</strong>?
          </div>
        </template>
      </GTModal>
    </teleport>
    <div v-if="isMobileView">
      <el-drawer v-if="isClient" v-model="isMobileMenuOpened" :show-close="false" size="100%" style="background-color: #0B112C; min-height: 400px;">
        <template #header="{ close }">
          <NuxtLinkLocale to="/" class="ml-1" @click="close">
            <IconGtLogo class="text-white max-w-[150px]" />
          </NuxtLinkLocale>
          <div class="cursor-pointer ml-2 mr-4 relative" @click="isNotificationsOpen = true">
            <div v-if="notifications.length" class="bg-red text-sm flex items-center justify-center text-white font-light rounded-full w-5 h-5 bottom-[10px] absolute right-0 left-3 z-10">
              <p>{{ notifications?.length }}</p>
            </div>
            <IconBell class="z-20 size-6 text-white relative" />
          </div>
          <button class="mr-1 bg-white hover:bg-gray-100 active:bg-gray-200 transition rounded-full p-2" @click="close">
            <IconCloseButton class="size-6" />
          </button>
        </template>
        <template #default>
          <div class="text-white font-redhat text-3xl grid gap-4">
            <div>
              <NuxtLinkLocale to="/" class="ml-1" @click="isMobileMenuOpened = false">
                {{ $t('home') }}
              </NuxtLinkLocale>
            </div>
            <div>
              <NuxtLinkLocale
                :to="{
                  name: 'lawyers-specialty',
                  params: { specialty: $t('advocates').toLowerCase() }
                }"
                class="ml-1"
                @click="isMobileMenuOpened = false"
              >
                {{ $t('lawyers') }}
              </NuxtLinkLocale>
            </div>
            <div>
              <NuxtLinkLocale
                v-if="!isClient || !user?.hasRole('ROLE_LAWYER')"to="forLawyer" class="ml-1" @click="isMobileMenuOpened = false"
              >
                {{ $t('are_you_a_lawyer') }}
              </NuxtLinkLocale>
            </div>
            <div v-if="user?.hasRole('ROLE_USER')">
              <NuxtLinkLocale to="services" class="ml-1" @click="isMobileMenuOpened = false">
                {{ $t('my_services_title') }}
              </NuxtLinkLocale>
            </div>
            <div v-if="user">
              <button class="flex items-center" @click="isMobileProfileExpanded = !isMobileProfileExpanded">
                <el-avatar :src="getUpdatedPhotoUrl(user.photo)">
                  <IconBlankUser alt="reviewer" class="scale-75" />
                </el-avatar>
                <div class="ml-3">
                  {{ $t('profile') }}
                </div>
                <div class="ml-20">
                  <component :is="!isMobileProfileExpanded ? IconPlus : IconMinus" class="size-4" />
                </div>
              </button>
              <transition>
                <div v-if="isMobileProfileExpanded" class="mt-6 text-lg">
                  <div>
                    <NuxtLinkLocale to="account-index" @click="isMobileMenuOpened = !isMobileMenuOpened">
                      {{ $t('settings') }}
                    </NuxtLinkLocale>
                  </div>
                  <div v-if="user?.hasRole('ROLE_LAWYER')" class="mt-4">
                    <NuxtLinkLocale to="account-index-profile" @click="isMobileMenuOpened = !isMobileMenuOpened">
                      {{ $t('lawyer_profile') }}
                    </NuxtLinkLocale>
                  </div>
                  <div class="border-b border-[#e8eaee] mt-6 mb-6" />
                  <button class="flex items-center" @click="confirmLogoutVisible = true">
                    <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 18.0552C1.45 18.0552 0.979167 17.8593 0.5875 17.4677C0.195833 17.076 0 16.6052 0 16.0552V2.05518C0 1.50518 0.195833 1.03434 0.5875 0.642676C0.979167 0.251009 1.45 0.0551758 2 0.0551758H9V2.05518H2V16.0552H9V18.0552H2ZM13 14.0552L11.625 12.6052L14.175 10.0552H6V8.05518H14.175L11.625 5.50518L13 4.05518L18 9.05518L13 14.0552Z" fill="#FFFFFF" />
                    </svg>
                    <div class="ml-2">
                      {{ $t('logout') }}
                    </div>
                  </button>
                </div>
              </transition>
            </div>
            <div v-else class="w-full">
              <div class="flex justify-center ">
                <NuxtLinkLocale
                  to="login"
                  class="absolute bottom-[112px] h-12 w-[327px] rounded-full bg-white text-darkBlue text-lg flex justify-center items-center"
                >
                  {{ $t('login') }}
                </NuxtLinkLocale>
              </div>
            </div>
            <div v-if="availableLocales && locale" class="absolute bottom-5 right-6 w-[60px]">
              <el-select v-model="locale">
                <el-option v-for="conreteLocale in availableLocales" :key="conreteLocale" :label="conreteLocale" :value="conreteLocale" />
              </el-select>
            </div>
          </div>
        </template>
      </el-drawer>
      <div class="h-[72px] transparent flex items-center w-full">
        <NuxtLinkLocale to="/" class="ml-6">
          <IconGtLogo class="w-[150px] transition duration-[400]" :class="[isHomePage ? 'text-white' : 'text-darkBlue']" />
        </NuxtLinkLocale>
        <div class="ml-auto mr-6">
          <button class="transition duration-[400]" :class="[isHomePage ? 'text-white' : 'text-darkBlue']" @click="isMobileMenuOpened = true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 18V16H21V18H3ZM3 13V11H21V13H3ZM3 8V6H21V8H3Z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="h-[88px] sticky top-0 flex items-center leading-none">
        <div class="flex items-center w-full h-full">
          <NuxtLinkLocale to="/" class="flex items-center h-full">
            <IconGtLogo class="text-darkBlue w-full max-w-[212px]"/>
          </NuxtLinkLocale>
          <div class="flex items-center h-full ml-auto text-md">
            <div class="md:flex">
              <NuxtLinkLocale
                v-if="isClient && user?.hasRole('ROLE_USER')"
                to="services"
                class="mr-6 text-darkBlue"
              >
                {{ $t('my_services_title') }}
              </NuxtLinkLocale>
              <NuxtLinkLocale
                v-if="!isClient || !user?.hasRole('ROLE_LAWYER')"
                to="forLawyer"
                class="mr-6 text-darkBlue"
              >
                {{ $t('are_you_a_lawyer') }}
              </NuxtLinkLocale>
              <NuxtLinkLocale
                :to="{
                  name: 'lawyers-specialty',
                  params: { specialty: $t('advocates').toLowerCase() }
                }"
                class="mr-6 text-darkBlue"
              >
                {{ $t('lawyers') }}
              </NuxtLinkLocale>
              <div v-if="availableLocales && locale" class="text-darkBlue mr-6">
                <GTDropdown :title="locale" labelClass="first-letter:capitalize">
                  <template #default="{ closeDropdown }">
                    <GTDropdownOptions
                      class="min-w-[50px]"
                      :data="availableLocales.map(x => ({ id: x, label: x }))"
                      labelClass="first-letter:capitalize"
                      @change="({ id }) => {
                        closeDropdown()
                        handleLocaleChange(id)
                      }"
                    />
                  </template>

                </GTDropdown>
              </div>
            </div>
            <ClientOnly>
              <template v-if="isSessionAvailable">
                <el-popover
                  ref="notificationPopoverRef"
                  placement="bottom-end"
                  :show-arrow="false"
                  :width="500"
                  trigger="click"
                  popper-style="border-radius: 20px; top: 75px;"
                  popper-class="hidden lg:block"
                >
                  <template #reference>
                    <div class="cursor-pointer ml-2 mr-4 relative">
                      <div v-if="notifications.length" class="bg-red text-sm flex items-center justify-center text-white font-light rounded-full w-5 h-5 bottom-[10px] absolute right-0 left-3 z-[-5]">
                        <p>{{ notifications?.length }}</p>
                      </div>
                      <IconBell class="size-6 text-darkBlue" />
                    </div>
                  </template>
                  <template #default>
                    <div class="w-full flex text-darkBlue justify-between items-center px-2">
                      <h1 class="text-xl ">{{ t('notifications') }}</h1>
                      <button v-if="notifications.length" class="flex text-base items-center gap-1" @click="handleClearAll">
                        <IconDoubleCheck class="size-5" />
                        {{ t('clear_all') }}
                      </button>
                    </div>
                    <div v-if="!notifications.length" class="w-full h-48 flex items-center justify-center text-lg">
                      {{ t('there_are_no_notifications') }}
                    </div>
                    
                    <div v-else class="flex flex-col gap-2 px-2 mt-2">
                      <div 
                        v-for="notification in notifications" :key="notification.created_at"
                        class="hover:bg-gray hover:rounded-md hover:cursor-pointer p-2 flex items-center justify-between group"
                        @click="handleRedirectToService(notification.visit_id, notification.id)"
                      >
                        <div class="flex gap-2">
                          <el-avatar :src="getUpdatedPhotoUrl(notification.photo)">
                            <IconBlankUser alt="reviewer" class="scale-75" />
                          </el-avatar>
                          <div class="flex flex-col">
                            <p><span class="font-bold text-darkBlue">{{ notification.name }} {{ notification.lastname }}</span> {{ t(`${notification.action}_notification`) }} {{ t('meeting2').toLowerCase() }}</p>
                            <p class="text-lightBlue">{{ formatDateTimeFromNow(notification.created_at, locale) }}</p>
                          </div>
                        </div>

                         <button 
                         class="flex items-center text-white px-2 py-2 justify-center rounded-lg gap-1 bg-lightBlue opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                         @click.stop="handleCleanButton(notification.id)"
                         >
                          <ElIconCheck class="text-white size-4" />
                          {{ t('clear') }}
                         </button>
                      </div>
                    </div>
                  </template>
                </el-popover>
                <el-popover
                  ref="profilePopoverRef"
                  placement="bottom-end"
                  :show-arrow="false"
                  :width="240"
                  trigger="click"
                  popper-style="border-radius: 20px; top: 75px;"
                  popper-class="hidden lg:block"
                >
                  <template #reference>
                    <button>
                      <div v-if="session?.user" class="profile-button pt-2 pr-0 pb-2 pl-1">
                        <el-avatar :src="getUpdatedPhotoUrl(session?.user.photo)">
                          <IconBlankUser alt="reviewer" class="scale-75" />
                        </el-avatar>
                      </div>
                    </button>
                  </template>
                  <template #default>
                    <div class="popup-triangle" />
                    <div id="profile-popup-content-container">
                      <div class="flex justify-center mt-3">
                        <el-avatar :src="getUpdatedPhotoUrl(session?.user.photo)" :size="80">
                          <IconBlankUser alt="reviewer" class="scale-75" />
                        </el-avatar>
                      </div>
                      <div class="flex justify-center mt-3 text-lg text-darkBlue">
                        {{ session?.user.name }} {{ session?.user.lastname }}
                      </div>
                      <div class="border-b border-[#e8eaee] mt-4 mb-4" />
                      <div>
                        <NuxtLinkLocale to="account-index" class="ml-1 p-2 text-md text-darkBlue" @click="profilePopoverRef.hide()">
                          {{ $t('settings') }}
                        </NuxtLinkLocale>
                      </div>
                      <div v-if="user?.hasRole('ROLE_LAWYER')" class="mt-4">
                        <NuxtLinkLocale to="account-index-profile" class="ml-1 p-2 text-md text-darkBlue" @click="profilePopoverRef.hide()">
                          {{ $t('lawyer_profile') }}
                        </NuxtLinkLocale>
                      </div>
                      <div class="border-b border-[#e8eaee] mt-4" />
                      <button class="ml-3 mb-3 flex" @click="confirmLogoutVisible = true">
                        <svg
                          class="mt-4.5"
                          width="18"
                          height="19"
                          viewBox="0 0 18 19"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M2 18.0552C1.45 18.0552 0.979167 17.8593 0.5875 17.4677C0.195833 17.076 0 16.6052 0 16.0552V2.05518C0 1.50518 0.195833 1.03434 0.5875 0.642676C0.979167 0.251009 1.45 0.0551758 2 0.0551758H9V2.05518H2V16.0552H9V18.0552H2ZM13 14.0552L11.625 12.6052L14.175 10.0552H6V8.05518H14.175L11.625 5.50518L13 4.05518L18 9.05518L13 14.0552Z" fill="#0E1535" />
                        </svg>
                        <span class="text-md text-darkBlue mt-2 pl-2 pt-2 pr-2">{{ $t('logout') }}</span>
                      </button>
                    </div>
                  </template>
                </el-popover>
              </template>
              <template v-else>
                <div class="flex items-center text-white">
                  <NuxtLinkLocale
                    to="login"
                    class="login-button ml-2 py-2 px-4 max-w-[105px] h-[40px] rounded-[9999px] inline-flex items-center justify-center w-full bg-darkBlue font-normal"
                  >
                    {{ $t('login') }}
                  </NuxtLinkLocale>
                </div>
              </template>
            </ClientOnly>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Teleport v-if="isMobileView" to="body">
    <Transition name="fade">
      <div v-if="isNotificationsOpen" class="fixed top-0 left-0 h-full w-full z-[12000] bg-white flex flex-col gap-3 font-normal">
        <div class="p-6 pb-0 flex font-semibold gap-2.5 items-center text-darkBlue">
          <button class="p-1" @click="isNotificationsOpen = false">
            <IconArrowFullLeft class="size-6" />
          </button>
          <h1 class="text-2xl pb-1">{{ t('notifications') }}</h1>
          <button v-if="notifications.length" class="flex ml-auto pt-[2px] text-base items-center gap-1" @click="handleClearAll">
            <IconDoubleCheck class="size-5" />
            {{ t('clear_all') }}
          </button>
        </div>
        <div v-if="!notifications.length" class="px-6 w-full h-full font-semibold flex items-center justify-center text-lg">
          {{ t('there_are_no_notifications') }}
        </div>

        <div v-else class="px-6 text-[13px] xs:text-base flex flex-col gap-2 mt-2">
          <div 
            v-for="notification in notifications" :key="notification.created_at"
            class="hover:bg-gray w-full hover:rounded-md hover:cursor-pointer gap-1 justify-between p-2 flex items-center group"
            @click="handleRedirectToService(notification.visit_id, notification.id)"
          >
            <div class="flex gap-2">
              <el-avatar :src="getUpdatedPhotoUrl(notification.photo)">
                <IconBlankUser alt="reviewer" class="scale-75" />
              </el-avatar>
              <div class="flex flex-col font-semibold">
                <p><span class="font-bold text-darkBlue">{{ notification.name }} {{ notification.lastname }}</span> {{
                  t(`${notification.action}_notification`) }} {{ t('consultation').toLowerCase() }}</p>
                <p class="text-lightBlue">{{ formatDateTimeFromNow(notification.created_at, locale) }}</p>
              </div>
            </div>
            <ElIconCloseBold class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-lightBlue size-6" @click.stop="handleCleanButton(notification.id)"/>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
<style scoped>
/* Outer triangle (gray border) */
.popup-triangle::before {
  content: '';
  position: absolute;
  top: -22px;
  right: 16px;
  border-style: solid;
  border-width: 11px 5px 11px 14px;
  border-color: transparent transparent rgb(228, 231, 237) transparent;
}

/* Inner triangle (white) */
.popup-triangle::after {
  content: '';
  position: absolute;
  top: -19px;
  right: 17px;
  border-style: solid;
  border-width: 10px 4px 10px 14px;
  border-color: transparent transparent #FFFFFF transparent;
}

/* transition settings*/
.v-enter-active,
.v-leave-active {
  transition: opacity 0.75s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
